import { Bell, Info } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { NotificationPermissionAnimation } from './notification-permission-animation';

interface NotificationPermissionModalProps {
  open: boolean;
  onEnable: () => void;
  onDismiss: () => void;
}

export const NotificationPermissionModal: React.FC<NotificationPermissionModalProps> = ({
  open,
  onEnable,
  onDismiss,
}) => {
  const { t } = useTranslation('authenticated.notifications');

  return (
    <Dialog open={open} onOpenChange={isOpen => !isOpen && onDismiss()}>
      <DialogContent className="max-w-[560px] border-none outline-none">
        <DialogHeader className="space-y-4">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Bell className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center">{t('permissionModal.title')}</DialogTitle>
          <DialogDescription className="whitespace-pre-line text-center">
            {t('permissionModal.description')}
          </DialogDescription>
        </DialogHeader>

        <NotificationPermissionAnimation />

        <Alert variant="info">
          <Info className="h-4 w-4" />
          <AlertDescription className="text-xs">
            <Trans
              i18nKey="authenticated.notifications:permissionModal.browserHint"
              components={{ bold: <strong /> }}
            />
          </AlertDescription>
        </Alert>

        <DialogFooter className="mt-2 sm:justify-center">
          <Button variant="secondary" onClick={onDismiss}>
            {t('permissionModal.dismiss')}
          </Button>
          <Button onClick={onEnable}>{t('permissionModal.enable')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
